import React, { useState } from "react";

const Checkbox = ({ checked, indeterminate }) => (
    <div style={{
        width: "16px", height: "16px", borderRadius: "3px", flexShrink: 0,
        border: checked || indeterminate ? "none" : "1.5px solid #ccc",
        backgroundColor: checked || indeterminate ? "#0DDFAB" : "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "background 0.15s",
    }}>
        {checked && (
            <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                <path d="M1 4L3.5 6.5L9 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        )}
        {!checked && indeterminate && (
            <div style={{ width: "8px", height: "2px", backgroundColor: "#fff", borderRadius: "1px" }} />
        )}
    </div>
);

const UserMultiSelect = ({
    options = [],
    value = [],
    onChange,
    placeholder = "Selected users will appear here...",
    label,
    searchPlaceholder = "Search users...",
    showAvatar = true,
    allowSelectAll = false,
    hideSelectedWhenEmpty = false,
    noInternalPanel = false,
}) => {
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const showRightPanel = !noInternalPanel && (!hideSelectedWhenEmpty || value.length > 0);

    const filteredOptions = options.filter(
        opt => opt.label.toLowerCase().includes(search.toLowerCase())
    );

    const isSelected = (opt) => value.some(v => v.value === opt.value);

    const allSelected = options.length > 0 && options.every(opt => value.some(v => v.value === opt.value));
    const someSelected = value.length > 0 && !allSelected;

    const handleToggle = (opt) => {
        if (isSelected(opt)) {
            onChange(value.filter(v => v.value !== opt.value));
        } else {
            onChange([...value, opt]);
        }
    };

    const handleSelectAll = () => {
        if (allSelected) {
            onChange([]);
        } else {
            onChange(options);
        }
    };

    const handleRemove = (optValue) => {
        onChange(value.filter(v => v.value !== optValue));
    };

    const emptyLabel = label ? `No ${label.toLowerCase()} found` : "No results found";

    return (
        <div style={{ position: "relative" }}>
            {/* Floating label */}
            {label && (
                <label style={{
                    position: "absolute",
                    top: (open || value.length > 0 || search !== "") ? "-9px" : "50%",
                    left: "12px",
                    transform: (open || value.length > 0 || search !== "") ? "translateY(0)" : "translateY(-50%)",
                    backgroundColor: "#fff",
                    padding: "0 4px",
                    fontSize: (open || value.length > 0 || search !== "") ? "11px" : "14px",
                    color: (open || value.length > 0 || search !== "") ? "#1D1D1D" : "#aaa",
                    zIndex: 2,
                    pointerEvents: "none",
                    transition: "all 0.2s ease",
                    lineHeight: 1,
                }}>
                    {label}
                </label>
            )}

            {/* Input-like container */}
            <div style={{
                display: "flex",
                alignItems: "stretch",
                border: `1.5px solid ${open ? "#1D1D1D" : "#d0d0d0"}`,
                borderRadius: "8px",
                overflow: "visible",
                position: "relative",
                transition: "border-color 0.2s ease",
                minHeight: "40px",
            }}>
                {/* Left: Search input */}
                <div style={{ width: showRightPanel ? "42%" : "100%", borderRight: showRightPanel ? "1px solid #ebebeb" : "none", flexShrink: 0, position: "relative", minHeight: "40px" }}>
                    <i className="uil uil-search" style={{
                        position: "absolute", right: "14px", top: "20px",
                        transform: "translateY(-50%)", color: open ? "#1D1D1D" : "#aaa", fontSize: "14px", pointerEvents: "none",
                    }} />
                    <input
                        type="text"
                        className="user-multiselect-search"
                        placeholder={!label || open || value.length > 0 || search !== "" ? searchPlaceholder : ""}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        onFocus={() => setOpen(true)}
                        onBlur={() => { setOpen(false); setSearch(""); }}
                        style={{
                            width: "100%",
                            height: "40px",
                            padding: "0 36px 0 14px",
                            fontSize: "14px",
                            border: "none",
                            outline: "none",
                            boxShadow: "none",
                            background: "transparent",
                            color: "#1D1D1D",
                            borderRadius: "8px 0 0 8px",
                        }}
                    />
                </div>

                {/* Right: Selected tags */}
                {showRightPanel && <div style={{
                    flex: 1,
                    padding: "5px 12px",
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: "5px",
                    borderRadius: "0 8px 8px 0",
                }}>
                    {value.length === 0 ? (
                        <span style={{ color: "#aaa", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{placeholder}</span>
                    ) : (
                        value.map(v => (
                            <span key={v.value} style={{
                                display: "inline-flex", alignItems: "center", gap: "4px", flexShrink: 0,
                                backgroundColor: "#f0fdf9", border: "1px solid #b2f5e8",
                                borderRadius: "4px", padding: "2px 7px", fontSize: "12px", color: "#333"
                            }}>
                                {v.label}
                                <button
                                    type="button"
                                    onMouseDown={e => e.preventDefault()}
                                    onClick={() => handleRemove(v.value)}
                                    style={{
                                        border: "none", background: "none", cursor: "pointer",
                                        padding: "0", lineHeight: 1, color: "#888", fontSize: "15px",
                                        display: "flex", alignItems: "center"
                                    }}
                                >×</button>
                            </span>
                        ))
                    )}
                </div>}

                {/* Dropdown */}
                {open && (
                    <div
                        onMouseDown={e => e.preventDefault()}
                        style={{
                            position: "absolute",
                            top: "calc(100% + 4px)",
                            left: "-1.5px",
                            width: "calc(42% + 3px)",
                            border: "1.5px solid #1D1D1D",
                            borderRadius: "8px",
                            backgroundColor: "#fff",
                            zIndex: 1000,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                            overflow: "hidden",
                        }}
                    >
                        {/* Select All row */}
                        {allowSelectAll && options.length > 0 && (
                            <div
                                onClick={handleSelectAll}
                                style={{
                                    display: "flex", alignItems: "center", gap: "8px",
                                    padding: "7px 10px", cursor: "pointer",
                                    backgroundColor: allSelected ? "#f0fdf9" : "transparent",
                                    borderBottom: "1px solid #f0f0f0",
                                    userSelect: "none",
                                    fontWeight: 600,
                                }}
                                onMouseEnter={e => { if (!allSelected) e.currentTarget.style.backgroundColor = "#fafafa"; }}
                                onMouseLeave={e => { e.currentTarget.style.backgroundColor = allSelected ? "#f0fdf9" : "transparent"; }}
                            >
                                <Checkbox checked={allSelected} indeterminate={someSelected} />
                                <span style={{ fontSize: "13px", color: "#1D1D1D" }}>
                                    All {label || ""}
                                </span>
                            </div>
                        )}

                        <div style={{ maxHeight: "158px", overflowY: "auto" }}>
                            {filteredOptions.length === 0 ? (
                                <div style={{ padding: "12px", color: "#aaa", fontSize: "13px", textAlign: "center" }}>
                                    {emptyLabel}
                                </div>
                            ) : (
                                filteredOptions.map(opt => {
                                    const selected = isSelected(opt);
                                    return (
                                        <div
                                            key={opt.value}
                                            onClick={() => handleToggle(opt)}
                                            style={{
                                                display: "flex", alignItems: "center", gap: "8px",
                                                padding: "7px 10px", cursor: "pointer",
                                                backgroundColor: selected ? "#f0fdf9" : "transparent",
                                                userSelect: "none",
                                            }}
                                            onMouseEnter={e => { if (!selected) e.currentTarget.style.backgroundColor = "#fafafa"; }}
                                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = selected ? "#f0fdf9" : "transparent"; }}
                                        >
                                            <Checkbox checked={selected} />
                                            {showAvatar && (
                                                <div style={{
                                                    width: "26px", height: "26px", borderRadius: "4px", flexShrink: 0,
                                                    backgroundColor: "#0DDFAB",
                                                    color: "#fff", fontSize: "10px", fontWeight: "600",
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                }}>
                                                    {opt.initials || opt.label?.[0]?.toUpperCase()}
                                                </div>
                                            )}
                                            <span style={{ fontSize: "13px", color: "#333" }}>{opt.label}</span>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserMultiSelect;
